import React, { FC, Fragment, useContext, useState, useRef } from 'react';
import { Box, Button } from '@mui/material';
import ProductionValueReportComponent, {
    ProductionValueReportRef,
} from 'components/shared/production-value-report/production-value-report.component';
import { restorationActionRowsByActionId } from 'utils/restoration-price-rows';
import { computeProductionValueSteps } from 'components/shared/production-value-report/production-value-report.utils';
import { useTranslation } from 'react-i18next';
import { ProductionValueReport } from 'models/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import { saveOrderProductionValueReportTemplate } from 'apollo/templates/order';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { currentUserVar } from 'apollo/reactive-variables/user';

const ProductionValueReportWrapper: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [productionValueReportData, setProductionValueReportData] = useState<{
        orderId: string;
        productionValueReports: ProductionValueReport[];
    }>();
    const [isValid, setIsValid] = useState(true);
    const { showBoundary } = useDenthubErrorBoundary();
    const { order } = useContext(OrderDetailsContext);
    const reportRef = useRef<ProductionValueReportRef>(null);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentUser = currentUserVar();

    const isTechnicianAdmin = currentUser.role === 'TECHNICIAN_ADMIN';

    const [saveOrderProductionValueReport] = useMutation(saveOrderProductionValueReportTemplate, {
        onCompleted: () => {
            reportRef.current?.refetchProductionValueReports();
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('orders:PRODUCTION_VALUE_REPORT_SAVED_SUCCESSFULLY'),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleValidationChange = (valid: boolean) => {
        if (order.isProductionFinished) {
            setIsValid(valid);
        }
    };

    const handleDataChange = (data: { orderId: string; productionValueReports: ProductionValueReport[] }) => {
        setProductionValueReportData(data);
    };

    const handleSave = () => {
        const actionRowMap = restorationActionRowsByActionId(order?.restorations);
        let hasMissingPriceSpec = false;
        const productionValueReports =
            productionValueReportData?.productionValueReports
                ?.flatMap((productionValueReport) =>
                    productionValueReport.actions.map((action) => {
                        const actionRow = actionRowMap.get(action.actionId.toString());

                        if (!actionRow) {
                            hasMissingPriceSpec = true;
                            return null;
                        }

                        const steps = computeProductionValueSteps(action, actionRow, productionValueReport.steps);

                        return {
                            actionId: action.actionId,
                            steps,
                        };
                    }),
                )
                .filter(Boolean) || [];

        if (hasMissingPriceSpec) {
            console.log('Missing price specification for one or more actions');
            return;
        }

        saveOrderProductionValueReport({
            variables: {
                orderId: productionValueReportData?.orderId,
                productionValueReports: productionValueReports,
            },
        });
    };

    return (
        <Fragment>
            <Box mb={2}>
                <ProductionValueReportComponent
                    ref={reportRef}
                    order={order}
                    isProductionFinished={order.isProductionFinished}
                    fromInternalPage={true}
                    isTechnicianAdmin={isTechnicianAdmin}
                    onDataChange={handleDataChange}
                    onValidationChange={handleValidationChange}
                />
            </Box>
            <Box>
                <Button
                    variant="contained"
                    data-test="production-value-report-wrapper.save-btn"
                    onClick={handleSave}
                    disabled={(order.isProductionFinished && !isTechnicianAdmin) || !isValid}
                >
                    {translate('common:SAVE')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default ProductionValueReportWrapper;
